import { createSlice, createAsyncThunk } from '@reduxjs/toolkit';
import { queryContract } from '../Components/functions/Contract';
import { getNibiruSDK, getContractAddresses } from '../config/network';

const nibiru = getNibiruSDK();

// --- Risk / margin constants (matching contract) ----------------------------
// sai-perps contracts/perp/src/constants.rs: LIQ_THRESHOLD_P = 90% (-90% pnl)
const LIQ_THRESHOLD_P = 0.90;
// sai-perps contracts/perp/src/fees/mod.rs calculate_liquidation_fee_components:
// a 5% liquidation closing fee + a 5% trigger fee, both on collateral. The
// pair's close/trigger fee config is not used for the liquidation price.
const LIQ_CLOSING_FEE_P = 0.05;
const LIQ_TRIGGER_FEE_P = 0.05;

const initialState = {
  liquidationPrice: 0,
  currentLeverage: 1,
  currentCollateral: 0,
  currentPrice: 0,
  dubaiPrice: 0,
  isLoading: false,
  error: null,
  tradeData: null,
  feeStructure: null
};

/**
 * Calculates liquidation price using the contract formula
 * (sai-perps borrowing/mod.rs get_trade_liquidation_price_with_fees), so the
 * pre-trade preview matches what the trade shows once it is registered.
 *
 * Definitions:
 *   P  = open_price
 *   L  = leverage
 *   C  = trade.collateral_amount (post-open-fee collateral)
 *   LT = LIQ_THRESHOLD_P = 0.90
 *   F  = fees_collateral = C * (5% liq closing + 5% trigger) + borrowing fees
 *
 * Liquidation price:
 *   remaining_collateral = LT * C - F
 *   liq_price_distance = (P / C / L) * remaining_collateral
 *   If long: liq_price = P - liq_price_distance
 *   If short: liq_price = P + liq_price_distance
 *   If liq_price < 0, it returns 0
 *
 * With no borrowing fees yet (a new trade) C cancels out:
 *   liq_price = P ∓ P * 0.80 / L
 * so the preview does not depend on how much of the deposit the open fee takes.
 */
function calculateLiquidationCorrect({
  entryPrice,
  collateral,
  leverage,
  isLong,
  borrowingFeesCollateral = 0,
}) {
  const P = Number(entryPrice);    // open_price
  const C = Number(collateral);    // collateral_amount
  const L = Number(leverage);      // leverage

  // Validate inputs
  if (
    !P || !C || !L ||
    Number.isNaN(P) || Number.isNaN(C) || Number.isNaN(L) ||
    P <= 0 || C <= 0 || L <= 0
  ) {
    return {
      liquidationPrice: 0,
      positionSize: 0,
      remainingCollateral: 0,
      closingFees: 0
    };
  }

  const positionSize = C * L;

  // F = liquidation closing fee + liquidation trigger fee + borrowing fees
  const F = C * (LIQ_CLOSING_FEE_P + LIQ_TRIGGER_FEE_P) + borrowingFeesCollateral;

  // remaining_collateral = LT * C - F
  const remainingCollateral = LIQ_THRESHOLD_P * C - F;

  // If remaining collateral is <= 0, position is immediately liquidatable
  if (remainingCollateral <= 0) {
    return {
      liquidationPrice: isLong ? P : 0, // Immediately liquidatable
      positionSize,
      remainingCollateral,
      closingFees: F
    };
  }

  // liq_price_distance = (P / C / L) * remaining_collateral
  // Simplifies to: (P * remaining_collateral) / (C * L)
  const liqPriceDistance = (P / C / L) * remainingCollateral;

  // Calculate liquidation price based on direction
  let liquidationPrice;
  if (isLong) {
    // Long: liq_price = P - liq_price_distance
    liquidationPrice = P - liqPriceDistance;
  } else {
    // Short: liq_price = P + liq_price_distance
    liquidationPrice = P + liqPriceDistance;
  }

  // If liq_price < 0, return 0
  if (liquidationPrice < 0) {
    liquidationPrice = 0;
  }

  return {
    liquidationPrice,
    positionSize,
    remainingCollateral,
    closingFees: F,
    liqPriceDistance
  };
}

// Fetch actual trade data with correct liquidation price
export const fetchTradeDataWithLiquidation = createAsyncThunk(
  'liquidation/fetchTradeData',
  async ({ trader, tradeIndex }, { rejectWithValue }) => {
    try {
      const message = {
        get_trade: {
          trader: trader,
          index: tradeIndex
        }
      };

      const result = await queryContract(
        getContractAddresses().perp,
        message,
        nibiru.endptTm
      );

      if (!result || !result.data) {
        throw new Error('No trade data returned');
      }

      return result.data;
    } catch (error) {
      console.error('Failed to fetch trade data:', error);
      return rejectWithValue(error.message);
    }
  }
);

// Fetch fee structure from contract
export const fetchFeeStructure = createAsyncThunk(
  'liquidation/fetchFeeStructure',
  async ({ feeIndex = "FeeIndex(0)" }, { rejectWithValue }) => {
    try {
      const message = {
        get_fees: {
          index: feeIndex
        }
      };

      const result = await queryContract(
        getContractAddresses().perp,
        message,
        nibiru.endptTm
      );

      return result;
    } catch (error) {
      console.error('Failed to fetch fee structure:', error);
      return rejectWithValue(error.message);
    }
  }
);

// Calculate liquidation price with contract fees
export const calculateEnhancedLiquidation = createAsyncThunk(
  'liquidation/calculateEnhanced',
  async ({ price, leverage, collateral, isLong, marketIndex, collateralIndex }, { getState, rejectWithValue, dispatch }) => {
    try {
      const state = getState();
      let feeStructure = state.liquidation.feeStructure;

      // Fetch fee structure if not already cached
      if (!feeStructure) {
        const feeResult = await dispatch(fetchFeeStructure({ feeIndex: "FeeIndex(0)" }));
        if (feeResult.type.endsWith('/fulfilled')) {
          feeStructure = feeResult.payload;
        }
      }

      // Perform calculation
      const numericPrice = Number(price);
      const numericLeverage = Number(leverage);
      const numericCollateral = Number(collateral);

      // Use unified, mathematically correct liquidation calculation (contract formula)
      const {
        liquidationPrice,
        positionSize,
        remainingCollateral,
        closingFees,
        liqPriceDistance
      } = calculateLiquidationCorrect({
        entryPrice: numericPrice,
        collateral: numericCollateral,
        leverage: numericLeverage,
        isLong,
        borrowingFeesCollateral: 0, // a new trade has no borrowing fees yet
      });

      // Log calculation details for debugging / auditing
      console.log('Liquidation calculation (contract formula):', {
        position: isLong ? 'LONG' : 'SHORT',
        openPrice: numericPrice,
        leverage: numericLeverage,
        collateral: numericCollateral,
        LIQ_THRESHOLD_P,
        closingFees,
        remainingCollateral,
        liqPriceDistance,
        positionSize,
        liquidationPrice,
        percentageFromEntry:
          numericPrice
            ? (((liquidationPrice - numericPrice) / numericPrice) * 100).toFixed(4) + '%'
            : '0%'
      });

      return {
        liquidationPrice,
        openPrice: numericPrice,
        leverage: numericLeverage,
        collateral: numericCollateral,
        feeStructure,
        calculationDetails: {
          closingFees,
          remainingCollateral,
          positionSize,
          liqPriceDistance
        }
      };
    } catch (error) {
      console.error('Failed to calculate enhanced liquidation:', error);
      return rejectWithValue(error.message);
    }
  }
);

// Simple liquidation calculation (fallback mechanism using contract formula)
const calculateSimpleLiquidation = ({ price, leverage, collateral, isLong }) => {
  const {
    liquidationPrice,
    positionSize,
    remainingCollateral,
    closingFees
  } = calculateLiquidationCorrect({
    entryPrice: price,
    collateral,
    leverage,
    isLong,
    borrowingFeesCollateral: 0,
  });

  return {
    liquidationPrice,
    openPrice: Number(price),
    leverage: Number(leverage),
    collateral: Number(collateral),
    positionSize,
    remainingCollateral,
    closingFees
  };
};

export const liquidationSlice = createSlice({
  name: 'liquidation',
  initialState,
  reducers: {
    // Liquidation calculation (for immediate UI updates)
    updateLiquidationPrice: (state, action) => {
      const result = calculateSimpleLiquidation(action.payload);
      
      state.liquidationPrice = result.liquidationPrice;
      state.currentPrice = result.openPrice;
      state.currentLeverage = result.leverage;
      state.currentCollateral = result.collateral;
    },
    
    // Set Dubai price
    setDubaiPrice: (state, action) => {
      state.dubaiPrice = action.payload;
      state.currentPrice = action.payload;
    },
    
    // Clear error
    clearError: (state) => {
      state.error = null;
    },
    
    // Reset liquidation state
    resetLiquidation: (state) => {
      state.liquidationPrice = 0;
      state.currentLeverage = 1;
      state.currentCollateral = 0;
      state.currentPrice = 0;
      state.error = null;
      state.tradeData = null;
    }
  },
  extraReducers: (builder) => {
    builder
      // Handle fetchTradeDataWithLiquidation
      .addCase(fetchTradeDataWithLiquidation.pending, (state) => {
        state.isLoading = true;
        state.error = null;
      })
      .addCase(fetchTradeDataWithLiquidation.fulfilled, (state, action) => {
        state.isLoading = false;
        state.tradeData = action.payload;
        
        // Use the EXACT liquidation price from contract
        if (action.payload.liquidation_price) {
          state.liquidationPrice = Number(action.payload.liquidation_price);
        }
        
        // Update other relevant state
        if (action.payload.trade) {
          state.currentLeverage = Number(action.payload.trade.leverage);
          state.currentCollateral = Number(action.payload.trade.collateral_amount) / 1000000;
          state.currentPrice = Number(action.payload.trade.open_price);
        }
      })
      .addCase(fetchTradeDataWithLiquidation.rejected, (state, action) => {
        state.isLoading = false;
        state.error = action.payload;
      })
      
      // Handle fetchFeeStructure
      .addCase(fetchFeeStructure.fulfilled, (state, action) => {
        state.feeStructure = action.payload;
      })
      .addCase(fetchFeeStructure.rejected, (state, action) => {
        console.error('Failed to fetch fee structure:', action.payload);
      })
      
      // Handle calculateEnhancedLiquidation
      .addCase(calculateEnhancedLiquidation.pending, (state) => {
        state.isLoading = true;
        state.error = null;
      })
      .addCase(calculateEnhancedLiquidation.fulfilled, (state, action) => {
        state.isLoading = false;
        state.liquidationPrice = action.payload.liquidationPrice;
        state.currentPrice = action.payload.openPrice;
        state.currentLeverage = action.payload.leverage;
        state.currentCollateral = action.payload.collateral;
        
        // Cache fee structure for future use
        if (action.payload.feeStructure) {
          state.feeStructure = action.payload.feeStructure;
        }
      })
      .addCase(calculateEnhancedLiquidation.rejected, (state, action) => {
        state.isLoading = false;
        state.error = action.payload;
        
        // Fallback to simple calculation on error
        console.warn('Enhanced calculation failed, using simple fallback');
      });
  }
});

export const { 
  updateLiquidationPrice, 
  setDubaiPrice, 
  clearError, 
  resetLiquidation 
} = liquidationSlice.actions;

export default liquidationSlice.reducer;